import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { endOfDay, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import CallexLoader from '../../../components/CallexLoader';
import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import Pagination from '../../../components/form/Pagination';
import AdminHeader from '../../../components/header/AdminHeader';
import FormDateInput from '../../../components/form/FormDateInput';
import BotCdrRecordingModal from './BotCdrRecordingModal';
import BotCdrTranscriptModal from './BotCdrTranscriptModal';
import {
  checkModulePermission,
  formatPayloadDate,
  getDateRangeFromLabel
} from '../../../utils/common';

import { useUserRolePermission } from '../../../store/slices/authSlice';
import { useTimezone } from '../../../store/slices/commonSlice';
import { callexAiAgentOptionsGet } from '../../../store/slices/tenantSlices/configurationSlice';
import {
  botArchiveListGet,
  botCdrsReportGet,
  botCdrsDownloadRecording,
  botCdrsSentimentAnalyse
} from '../../../store/slices/tenantSlices/dailyReportSlice';

const PERMISSION_KEY = 'AI CDRs';

// Recording path lives in this field; shown only as play/download actions, not a raw column.
const RECORDING_FIELD = 'recording_file';
// Columns handled via actions/modal and hidden from the plain dynamic table.
const HIDDEN_FIELDS = [RECORDING_FIELD, 'sentiment_analyse'];

const getNested = (obj, path) =>
  path?.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);

const renderCell = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const AiCdrReport = () => {
  const dispatch = useDispatch();
  const userRolePermission = useUserRolePermission();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [pagination, setPagination] = useState({});
  const [botOptions, setBotOptions] = useState([]);
  const [archiveOptions, setArchiveOptions] = useState([]);
  const [recordingFile, setRecordingFile] = useState(null);
  const [transcriptSession, setTranscriptSession] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);

  // Default to the current-month range so the first (mount) call matches the Month=current
  // selection (1st 00:00:01 → now), instead of just today.
  const currentMonthLabel = `${today.toLocaleString('default', { month: 'long' })}-${today.getFullYear()}`;
  const defaultRange = getDateRangeFromLabel(currentMonthLabel, timezone);

  const initialValues = {
    page: 1,
    limit: 10,
    startDate: defaultRange.minDate || startOfDay(today),
    endDate: defaultRange.maxDate || endOfDay(today),
    bot_id: '',
    destination_number: '',
    archiveId: 0
  };

  const validationSchema = Yup.object({
    startDate: Yup.date().required('Start date is required'),
    endDate: Yup.date().required('End date is required')
  });

  const onSubmit = async (valuesToUse) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const payload = {
        page: valuesToUse.page,
        limit: valuesToUse.limit,
        start_date: formatPayloadDate(valuesToUse.startDate),
        end_date: formatPayloadDate(valuesToUse.endDate),
        archiveId: valuesToUse.archiveId
      };
      if (valuesToUse.bot_id) payload.bot_id = valuesToUse.bot_id;
      if (valuesToUse.destination_number)
        payload.destination_number = valuesToUse.destination_number.trim();

      const response = await dispatch(botCdrsReportGet(payload)).unwrap();
      setRows(Array.isArray(response?.data) ? response.data : []);
      setHeaders(Array.isArray(response?.header) ? response.header : []);
      setPagination({
        total: response?.total ?? 0,
        page: response?.page ?? valuesToUse.page,
        limit: response?.limit ?? valuesToUse.limit
      });
    } catch (error) {
      console.log('error', error);
      setRows([]);
      setHeaders([]);
      setPagination({});
    } finally {
      setIsLoading(false);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit: (v) => onSubmit(v)
    });

  // Sentiment is computed on demand: trigger analysis for this session, then reload the
  // report so the row's sentiment_analyse object is populated.
  const handleAnalyseSentiment = async (sessionId) => {
    if (!sessionId || analyzingId) return;
    setAnalyzingId(sessionId);
    try {
      await dispatch(botCdrsSentimentAnalyse(sessionId)).unwrap();
      toast.success('Sentiment analysis complete');
      onSubmit(values);
    } catch (error) {
      console.log('error', error);
    } finally {
      setAnalyzingId(null);
    }
  };

  // Download fetches the recording as an authenticated blob and saves it via an object URL.
  const handleDownloadRecording = async (filePath) => {
    if (!filePath) return;
    try {
      const blob = await dispatch(botCdrsDownloadRecording({ filename: filePath })).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath.split('/').pop() || 'recording';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download failed');
      console.log('error', error);
    }
  };

  useEffect(() => {
    onSubmit(values);
  }, [values.limit, values.page]);

  const getBotOptions = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      setBotOptions(
        response?.data ? response.data.map((val) => ({ label: val.name, value: val.id })) : []
      );
    } catch (error) {
      setBotOptions([]);
      console.log('error', error);
    }
  };

  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(botArchiveListGet()).unwrap();
      const list = Array.isArray(response) ? response : response?.data || [];
      const currentLabel = `${today.toLocaleString('default', { month: 'long' })}-${today.getFullYear()}`;
      setArchiveOptions([
        { label: currentLabel, value: 0 },
        ...list.map((val) => ({ label: val.archive_name, value: val.id }))
      ]);
    } catch (error) {
      setArchiveOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getBotOptions();
    getArchiveOptions();
  }, []);

  // Month selection drives the allowed date range (same behavior as the Dump Report).
  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );
  const selectedLabel = selectedOption?.label || null;
  const { minDate, maxDate } = useMemo(
    () => getDateRangeFromLabel(selectedLabel, timezone),
    [selectedLabel, timezone]
  );

  useEffect(() => {
    if (minDate && maxDate) {
      setFieldValue('startDate', minDate);
      setFieldValue('endDate', maxDate);
    }
  }, [minDate?.getTime(), maxDate?.getTime()]);

  // Render every header column except those handled via actions (recording, sentiment).
  const visibleHeaders = headers.filter((col) => !HIDDEN_FIELDS.includes(col.name));

  return (
    <div>
      <AdminHeader
        title="AI CDRs Report"
        isSearch
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
      />

      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="startDate"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  value={values.startDate}
                  minDate={minDate}
                  maxDate={values.endDate || maxDate}
                  onChange={(e) => setFieldValue('startDate', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="endDate"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.startDate || minDate}
                  maxDate={maxDate}
                  value={values.endDate}
                  onChange={(e) => setFieldValue('endDate', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  name="bot_id"
                  label="Bot"
                  placeholder="Select Bot"
                  value={values.bot_id}
                  options={botOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormInput
                  name="destination_number"
                  label="Destination Number"
                  placeholder="Enter destination number"
                  value={values.destination_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : rows.length > 0 &&
        checkModulePermission('is_view', PERMISSION_KEY, userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 370px)' : 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {visibleHeaders.map((col) => (
                    <th key={col.name}>{col.label}</th>
                  ))}
                  <th className="text-center" style={{ width: '140px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const recFile = row?.[RECORDING_FIELD];
                  return (
                    <tr key={row?.session_id || index}>
                      {visibleHeaders.map((col) => (
                        <td key={col.name}>{renderCell(getNested(row, col.name))}</td>
                      ))}
                      <td className="text-center">
                        <div className="d-flex justify-content-center align-items-center gap-3">
                          {recFile ? (
                            <>
                              <Icon.PlayCircle
                                size={16}
                                title="Play recording"
                                className="cursor-pointer text-info"
                                onClick={() => setRecordingFile(recFile)}
                              />
                              <Icon.Download
                                size={16}
                                title="Download recording"
                                className="cursor-pointer"
                                onClick={() => handleDownloadRecording(recFile)}
                              />
                            </>
                          ) : null}
                          {row?.session_id ? (
                            <Icon.FileText
                              size={16}
                              title="Transcript"
                              className="cursor-pointer text-primary"
                              onClick={() =>
                                setTranscriptSession({
                                  sessionId: row.session_id,
                                  sentiment: row.sentiment_analyse || null
                                })
                              }
                            />
                          ) : null}
                          {row?.session_id ? (
                            row.sentiment_analyse ? (
                              <Icon.Activity
                                size={16}
                                title="Sentiment already analysed"
                                className="text-success"
                              />
                            ) : analyzingId === row.session_id ? (
                              <Icon.Loader
                                size={16}
                                title="Analysing…"
                                className="text-muted spin"
                              />
                            ) : (
                              <Icon.Activity
                                size={16}
                                title="Analyse sentiment"
                                className="cursor-pointer text-secondary"
                                onClick={() => handleAnalyseSentiment(row.session_id)}
                              />
                            )
                          ) : null}
                          {!recFile && !row?.session_id ? '-' : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={pagination?.total || 0}
              limit={pagination?.limit || values.limit}
              page={pagination?.page || values.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit(values)}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}

      <BotCdrRecordingModal
        isOpen={!!recordingFile}
        filePath={recordingFile}
        onClose={() => setRecordingFile(null)}
      />

      <BotCdrTranscriptModal
        isOpen={!!transcriptSession}
        sessionId={transcriptSession?.sessionId}
        sentiment={transcriptSession?.sentiment}
        archiveId={values.archiveId}
        onClose={() => setTranscriptSession(null)}
      />
    </div>
  );
};

export default AiCdrReport;
